// @flow
import {ofType, StateObservable} from 'redux-observable';
import {map, switchMap, debounceTime} from 'rxjs/operators';
import type {RootState} from '../index';
import {of} from 'rxjs';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';

import {
  createOrderApi,
  createPreOrderApi,
  editOrderApi,
  editPreOrderApi,
  getOrderSummaryApi,
  getShoppingCartPricesApi,
} from './api';
import type {ItemRequest} from './api';
import type {
  Product,
  ProductVariant,
  DeliveryMethodName,
  Pagination,
  Order,
  CrmCustomer,
  SCTax,
  OrderItemProductOption,
} from '../../entities';

import type {ActionsObservable} from 'redux-observable';
import type {Action} from '../../store/utils';
import {arrayToOrderedMap, orderedMapToArray} from '../../utils/array';
import {getProductsApi} from '../products/api';
import {getCustomersApi} from '../crm/api';
import {buildRoute} from '../../http';
import {endpoints} from '../manual-order/api';

import {cartSummaryAdapter} from '../order/adapters/summary-adapter';

import type {
  CartSummary,
  Summary,
  CartSummaryCoupon,
  CartSummaryItemPromotion,
  CartSummaryGlobalDiscount,
  CartSummaryItem,
  ApplicableCoupon,
  OrderGlobalDiscount,
  CreateOrderSummaryRequest,
  CreateOrderRequest,
} from '../../entities';
import {buildCartItemID} from './utils/build-cart-item-id';

export type Discount = OrderGlobalDiscount;

export type PreOrderCheckoutRequest = {
  items: ItemRequest[],
  coupon?: string,
  globalDiscount?: Discount,
};

export type ShoppingCartItem = {
  amount: number,
  comment?: string,
  product: Product,
  productVariant?: ProductVariant,
  discount?: Discount,
  id: string,
};

export type ShoppingCart = {
  items: ShoppingCartItem[],
  globalDiscount?: Discount,
  couponCode?: string,
};

export type ItemPrice = {
  product: string,
  productVariant: string,
  quantity: number,
  stock: number,
  price: {
    source: string,
    subTotal: string,
    unitPrice: string,
  },
  comment?: string,
  error?: string,
};

export type ShoppingCartPricesRequest = {
  items: {
    productId: string,
    productVariantId?: string,
    quantity: number,
    productOptions?: OrderItemProductOption[],
  }[],
  coupon?: string,
};

export type OrderSummaryShippingData = {
  flatRateId?: string,
  shippingAddress?: {
    address1: string,
    address2?: string,
    admin1?: string,
    admin2?: string,
    admin3?: string,
    country: string,
  },
  shippingAddressType: 'RAW' | 'ADMIN',
};

export type OrderSummaryPickupData = {
  pickupAddressId?: string,
};

export type OrderSummaryRequest = {
  storeId: string,
  items: {
    productId: string,
    productVariantId?: string,
    quantity: number,
  }[],
  shippingData?: OrderSummaryShippingData,
  pickupData?: OrderSummaryPickupData,
  deliveryMethod: DeliveryMethodName,
  coupon?: string,
};

export type MiniPromotion = {
  discount: string,
  enabledOfferPrice: boolean,
  name: string,
  promotionType: string,
  promotion: string,
};

export type MiniCoupon = {
  couponDiscount: string,
  couponCode: string,
};

export type ManualOrderAction = Action & {
  product?: Product,
  productVariant?: ProductVariant,
  comment?: string,
  discount?: Discount,
  globalDiscount?: Discount,
  couponCode?: string,
  amount?: number,
  items?: CartSummaryItem[],
  applicableCoupons?: ApplicableCoupon[],
  shoppingCart?: ShoppingCart,
  coupon?: CartSummaryCoupon,
  promotions?: CartSummaryItemPromotion[],
  shoppingCartPricesRequest?: ShoppingCartPricesRequest,
  orderSummary?: CartSummary,
  orderSummaryRequest?: CreateOrderSummaryRequest,
  checkoutRequest?: CreateOrderRequest,
  searchFilter?: {[key: string]: string},
  tempOrderInfo?: any,
  couponDiscount?: string,

  itemId?: string,
};

export type DiscountData = {
  discount: string,
  type: 'FIXED_DISCOUNT' | 'PERCENTAGE_DISCOUNT',
};

export type ManualOrderState = {
  products?: Product[],
  isFetchingProducts?: boolean,
  pagination?: Pagination,
  shoppingCart?: ShoppingCart,
  isFetchingPrices?: boolean,
  itemPrices: CartSummaryItem[],
  productItemPrices?: CartSummaryItem[],
  orderSummary?: Summary & {items: CartSummaryItem[]},
  subTotal?: string,
  total?: string,
  taxes?: SCTax[],
  applicableCoupons?: ApplicableCoupon[],
  coupon?: CartSummaryCoupon,
  couponDiscount?: string,
  promotions?: MiniPromotion[],
  isExecutingCheckout?: boolean,
  createOrderSuccess?: boolean,
  initializeCheckoutError?: string,
  isEditingOrder?: boolean,
  editOrderSuccess?: boolean,
  editOrderError?: boolean,
  order?: Order,
  error?: string,
  globalDiscountData?: CartSummaryGlobalDiscount,
  isFetchingCustomers?: boolean,
  errorFetchingCustomers?: string,
  customers?: Object[],
  tempOrderInfo?: any,
};

export class FetchManualOrderProducts extends ActionCreator<ManualOrderAction> {}
export class FetchManualOrderProductsSuccess extends ActionCreator<ManualOrderAction> {}
export class FetchManualOrderProductsError extends ActionCreator<ManualOrderAction> {}
export class SetManualOrderProducts extends ActionCreator<ManualOrderAction> {}
export class AddShoppingCartItem extends ActionCreator<ManualOrderAction> {}
export class RemoveShoppingCartItem extends ActionCreator<ManualOrderAction> {}
export class DeleteShoppingCartItem extends ActionCreator<ManualOrderAction> {}
export class AddGlobalDiscountShoppingCart extends ActionCreator<ManualOrderAction> {}
export class RemoveGlobalDiscountShoppingCart extends ActionCreator<ManualOrderAction> {}
export class AddDiscountShoppingCartItem extends ActionCreator<ManualOrderAction> {}
export class FetchShoppingCartPrices extends ActionCreator<ManualOrderAction> {}
export class FetchShoppingCartPricesSuccess extends ActionCreator<ManualOrderAction> {}
export class FetchShoppingCartPricesErrorFn extends ActionCreator<ManualOrderAction> {}
export class ResetShoppingCart extends ActionCreator<ManualOrderAction> {}
export class FetchProductPrice extends ActionCreator<ManualOrderAction> {}
export class FetchProductPriceSuccess extends ActionCreator<ManualOrderAction> {}
export class FetchProductPriceErrorFn extends ActionCreator<ManualOrderAction> {}
export class FetchOrderSummary extends ActionCreator<ManualOrderAction> {}
export class FetchOrderSummarySuccess extends ActionCreator<ManualOrderAction> {}
export class FetchOrderSummaryErrorFn extends ActionCreator<ManualOrderAction> {}
export class CreateOrder extends ActionCreator<ManualOrderAction> {}
export class CreateOrderSuccess extends ActionCreator<ManualOrderAction> {}
export class CreateOrderErrorFn extends ActionCreator<ManualOrderAction> {}
export class EditOrder extends ActionCreator<ManualOrderAction> {}
export class EditOrderSuccess extends ActionCreator<ManualOrderAction> {}
export class EditOrderErrorFn extends ActionCreator<ManualOrderAction> {}
export class CreatePreOrder extends ActionCreator<ManualOrderAction> {}
export class CreatePreOrderSuccess extends ActionCreator<ManualOrderAction> {}
export class CreatePreOrderErrorFn extends ActionCreator<ManualOrderAction> {}
export class EditPreOrder extends ActionCreator<ManualOrderAction> {}
export class EditPreOrderSuccess extends ActionCreator<ManualOrderAction> {}
export class EditPreOrderErrorFn extends ActionCreator<ManualOrderAction> {}
export class ResetManualOrderState extends ActionCreator<ManualOrderAction> {}
export class ResetProductsState extends ActionCreator<ManualOrderAction> {}
export class ResetProductsFilter extends ActionCreator<ManualOrderAction> {}

export class ApplyCoupon extends ActionCreator<ManualOrderAction> {}
export class RemoveCoupon extends ActionCreator<ManualOrderAction> {}

export class SearchCustomers extends ActionCreator<ManualOrderAction> {}
export class SearchCustomersSuccess extends ActionCreator<ManualOrderAction> {}
export class SearchCustomersErrorFn extends ActionCreator<ManualOrderAction> {}

export class SetTempOrderInfoFn extends ActionCreator<ManualOrderAction> {}

export const actions = {
  setTempOrderInfo: (payload: ManualOrderAction) =>
    new SetTempOrderInfoFn(payload),
  fetchProducts: (payload: ManualOrderAction) =>
    new FetchManualOrderProducts(payload),
  setProducts: (payload: ManualOrderAction) =>
    new SetManualOrderProducts(payload),
  addShoppingCartItem: (payload: ManualOrderAction) =>
    new AddShoppingCartItem(payload),
  removeShoppingCartItem: (payload: ManualOrderAction) =>
    new RemoveShoppingCartItem(payload),
  deleteShoppingCartItem: (payload: ManualOrderAction) =>
    new DeleteShoppingCartItem(payload),
  addGlobalDiscountShoppingCart: (payload: ManualOrderAction) =>
    new AddGlobalDiscountShoppingCart(payload),
  removeGlobalDiscountShoppingCart: (payload: ManualOrderAction) =>
    new RemoveGlobalDiscountShoppingCart(payload),
  addDiscountShoppingCartItem: (payload: ManualOrderAction) =>
    new AddDiscountShoppingCartItem(payload),
  applyCoupon: (payload: ManualOrderAction) => new ApplyCoupon(payload),
  removeCoupon: (payload: ManualOrderAction) => new RemoveCoupon(payload),
  fetchShoppingCartPrices: (payload: ManualOrderAction) =>
    new FetchShoppingCartPrices(payload),
  fetchProductPrice: (payload: ManualOrderAction) =>
    new FetchProductPrice(payload),
  resetShoppingCart: () => new ResetShoppingCart(),
  fetchOrderSummary: (payload: ManualOrderAction) =>
    new FetchOrderSummary(payload),
  createOrder: (payload: ManualOrderAction) => new CreateOrder(payload),
  editOrder: (payload: ManualOrderAction) => new EditOrder(payload),
  createPreOrder: (payload: ManualOrderAction) => new CreatePreOrder(payload),
  editPreOrder: (payload: ManualOrderAction) => new EditPreOrder(payload),
  resetState: () => new ResetManualOrderState(),
  resetProductsState: () => new ResetProductsState(),
  resetProductsFilter: () => new ResetProductsFilter(),
  searchCustomers: (payload: ManualOrderAction) => new SearchCustomers(payload),
};

export const initialState = {
  shoppingCart: {
    items: [],
  },
  itemPrices: [],
};

export default createReducer(initialState, {
  [SetTempOrderInfoFn.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => {
    return {
      ...state,
      tempOrderInfo: action.tempOrderInfo,
    };
  },
  [FetchManualOrderProducts.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    isFetchingProducts: true,
    fetchProductsError: undefined,
    products: action.reset ? undefined : state.products,
    pagination: action.reset ? undefined : state.pagination,
  }),
  [FetchManualOrderProductsSuccess.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => {
    let products;
    if (action.addPage) {
      const oldProducts = arrayToOrderedMap(state.products);
      const newProducts = arrayToOrderedMap(action.products);
      products = orderedMapToArray(oldProducts.merge(newProducts));
    } else {
      products = action.products;
    }
    return {
      ...state,
      isFetchingProducts: undefined,
      products,
      pagination: action.pagination,
    };
  },
  [FetchManualOrderProductsError.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    isFetchingProducts: undefined,
    fetchProductsError: action.error,
  }),
  [SetManualOrderProducts.type]: (state, {products}) => {
    return {
      ...state,
      products,
    };
  },
  [AddShoppingCartItem.type]: (
    state,
    {product, productVariant, amount, productOptions}
  ) => {
    const shoppingCart = {...state.shoppingCart};
    const itemId = buildCartItemID(
      product.id,
      productVariant?.id,
      productOptions?.map(option => ({
        optionId: option.productOptionId,
        optionGroupId: option.productOptionGroupId,
      }))
    );

    const foundIndex = shoppingCart.items.findIndex(i => {
      return i.id === itemId;
    });
    if (foundIndex === -1) {
      shoppingCart.items.push({
        id: itemId,
        product,
        productVariant,
        amount,
        productOptions,
      });
    } else {
      shoppingCart.items[foundIndex].amount += parseInt(amount);
      shoppingCart.items[foundIndex].productOptions = productOptions;
    }
    return {...state, shoppingCart};
  },
  /* THIS ACTION IS NOT USED  */
  [RemoveShoppingCartItem.type]: (
    state,
    {itemId, amount, openShoppingCartMenu = false}: ManualOrderAction
  ) => {
    const shoppingCart = {...state.shoppingCart};
    const foundIndex = shoppingCart.items.findIndex(i => i.id === itemId);
    if (foundIndex !== -1) {
      shoppingCart.items[foundIndex].amount -= amount;
      /*if (shoppingCart.items[foundIndex].amount === 0)
        shoppingCart.items.splice(foundIndex, 1);*/
    }
    return {...state, shoppingCart};
  },
  [DeleteShoppingCartItem.type]: (state, {itemId}: ManualOrderAction) => {
    const shoppingCart = {...state.shoppingCart};
    const itemPrices = [...state.itemPrices];
    const foundIndex = shoppingCart.items.findIndex(i => i.id === itemId);

    shoppingCart.items.splice(foundIndex, 1);
    if (itemPrices.length > shoppingCart.items.length)
      itemPrices.splice(foundIndex, 1);
    return {...state, shoppingCart, itemPrices};
  },
  [ResetShoppingCart.type]: () => {
    return {
      ...initialState,
    };
  },
  [AddDiscountShoppingCartItem.type]: (
    state,
    {itemId, discount, productVariant}: ManualOrderAction
  ) => {
    const shoppingCartCopy = {...state.shoppingCart};

    const itemIndex = shoppingCartCopy.items.findIndex(item => {
      return item.id === itemId;
    });
    if (itemIndex === -1) return state;
    shoppingCartCopy.items[itemIndex].discount = discount;
    return {...state, shoppingCart: shoppingCartCopy};
  },
  [AddGlobalDiscountShoppingCart.type]: (
    state,
    {globalDiscount}: ManualOrderAction
  ) => {
    const shoppingCartCopy = {...state.shoppingCart};
    shoppingCartCopy.globalDiscount = globalDiscount;
    return {...state, shoppingCart: shoppingCartCopy};
  },
  [RemoveGlobalDiscountShoppingCart.type]: (state: ManualOrderState) => {
    const shoppingCartCopy = {...state.shoppingCart};
    if (shoppingCartCopy.globalDiscount)
      shoppingCartCopy.globalDiscount = undefined;
    return {...state, shoppingCart: shoppingCartCopy};
  },
  [ApplyCoupon.type]: (state, {couponCode}: ManualOrderAction) => {
    const shoppingCartCopy = {...state.shoppingCart};
    shoppingCartCopy.couponCode = couponCode;
    return {...state, shoppingCart: shoppingCartCopy};
  },
  [RemoveCoupon.type]: (state: ManualOrderState) => {
    const shoppingCartCopy = {...state.shoppingCart};
    if (shoppingCartCopy.couponCode) shoppingCartCopy.couponCode = undefined;
    return {...state, shoppingCart: shoppingCartCopy, coupon: undefined};
  },
  [FetchShoppingCartPrices.type]: (state: ManualOrderState) => ({
    ...state,
    isFetchingPrices: true,
    itemPrices: initialState.itemPrices,
    total: undefined,
    coupon: undefined,
    promotions: undefined,
    subTotal: undefined,
    taxes: undefined,
    applicableCoupons: undefined,
  }),
  [FetchShoppingCartPricesSuccess.type]: (
    state: ManualOrderState,
    {
      items,
      total,
      subTotal,
      coupon,
      promotions,
      globalDiscountData,
      taxes,
      applicableCoupons,
      couponDiscount,
    }: ManualOrderAction
  ) => {
    return {
      ...state,
      isFetchingPrices: undefined,
      itemPrices: items,
      total,
      subTotal,
      promotions,
      coupon,
      globalDiscountData,
      error: undefined,
      taxes,
      couponDiscount,
      applicableCoupons,
    };
  },
  [FetchShoppingCartPricesErrorFn.type]: (
    state: ManualOrderState,
    {error}: ManualOrderAction
  ) => {
    const errorMessage =
      error === 'USAGE_LIMIT_ERROR'
        ? 'Se alcanzó el limite del cupón'
        : 'No se pudo aplicar el cupón, porfavor revisar la configuración del cupón';

    return {
      ...state,
      isFetchingPrices: undefined,
      itemPrices: [],
      error: errorMessage,
      total: undefined,
      coupon: undefined,
      promotions: [],
      taxes: undefined,
      applicableCoupons: undefined,
      subTotal: undefined,
      globalDiscountData: undefined,
    };
  },
  [FetchProductPrice.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    isFetchingPrices: true,
  }),
  [FetchProductPriceSuccess.type]: (
    state: ManualOrderState,
    {items}: ManualOrderAction
  ) => {
    return {
      ...state,
      isFetchingPrices: undefined,
      productItemPrices: items,
    };
  },
  [FetchProductPriceErrorFn.type]: (
    state: ManualOrderState,
    {error}: ManualOrderAction
  ) => ({
    ...state,
    isFetchingPrices: undefined,
    error,
  }),
  [FetchOrderSummary.type]: (
    state: ManualOrderState,
    payload: ManualOrderAction
  ) => ({
    ...state,
    isFetchingSummary: true,
    // itemPrices: undefined,
  }),
  [FetchOrderSummarySuccess.type]: (
    state: ManualOrderState,
    {orderSummary}: ManualOrderAction
  ) => {
    const {
      promotions,
      coupon,
      total,
      subTotal,
      couponDiscount,
      taxes,
      items,
      applicableCoupons,
    } = cartSummaryAdapter(orderSummary);
    // const couponDiscount =
    //   orderSummary.coupon && orderSummary.coupon.totalDiscount;
    // const couponCode = orderSummary.coupon && orderSummary.coupon.code;

    // const coupon =
    //   couponDiscount && couponCode ? {couponDiscount, couponCode} : undefined;
    return {
      ...state,
      isFetchingSummary: undefined,
      orderSummary: {...orderSummary.summary, items},
      promotions,
      couponDiscount,
      coupon,
      total,
      subTotal,
      taxes,
      applicableCoupons,
    };
  },
  [FetchOrderSummaryErrorFn.type]: (
    state: ManualOrderState,
    {error}: ManualOrderAction
  ) => ({
    ...state,
    isFetchingSummary: undefined,
    orderSummary: undefined,
    subTotal: undefined,
    total: undefined,
    promotions: undefined,
    coupon: undefined,
    taxes: undefined,
    error,
  }),
  [CreateOrder.type]: (state: ManualOrderState) => ({
    ...state,
    isExecutingCheckout: true,
    createOrderSuccess: undefined,
    initializeCheckoutError: undefined,
  }),
  [CreateOrderSuccess.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    order: action.data,
    isExecutingCheckout: undefined,
    createOrderSuccess: true,
  }),
  [CreateOrderErrorFn.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    isExecutingCheckout: undefined,
    initializeCheckoutError: action.error,
  }),
  [EditOrder.type]: (state: ManualOrderState) => ({
    ...state,
    isEditingOrder: true,
    editOrderSuccess: undefined,
    editOrderError: undefined,
  }),
  [EditOrderSuccess.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    isEditingOrder: undefined,
    editOrderSuccess: true,
  }),
  [EditOrderErrorFn.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    isEditingOrder: undefined,
    editOrderError: action.error,
  }),
  [CreatePreOrder.type]: (state: ManualOrderState) => ({
    ...state,
    isExecutingCheckout: true,
    createOrderSuccess: undefined,
    initializeCheckoutError: undefined,
  }),
  [CreatePreOrderSuccess.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    order: action.data,
    isExecutingCheckout: undefined,
    createOrderSuccess: true,
  }),
  [CreatePreOrderErrorFn.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    isExecutingCheckout: undefined,
    initializeCheckoutError: action.error,
  }),
  [EditPreOrder.type]: (state: ManualOrderState) => ({
    ...state,
    isEditingOrder: true,
    editOrderSuccess: undefined,
    editOrderError: undefined,
  }),
  [EditPreOrderSuccess.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    isEditingOrder: undefined,
    editOrderSuccess: true,
  }),
  [EditPreOrderErrorFn.type]: (
    state: ManualOrderState,
    action: ManualOrderAction
  ) => ({
    ...state,
    isEditingOrder: undefined,
    editOrderError: action.error,
  }),
  [ResetManualOrderState.type]: () => ({
    shoppingCart: {
      items: [],
    },
    itemPrices: [],
  }),
  [ResetProductsState.type]: (state: ManualOrderState) => ({
    ...state,
    isFetchingProducts: undefined,
    fetchProductsError: undefined,
    products: undefined,
    pagination: undefined,
  }),
  [ResetProductsFilter.type]: (state: ManualOrderState) => ({
    ...state,
    products: undefined,
  }),
  [SearchCustomers.type]: (state: ManualOrderState) => ({
    ...state,
    isSearchingCustomers: true,
  }),
  [SearchCustomersSuccess.type]: (state: ManualOrderState, action) => {
    return {
      ...state,
      isSearchingCustomers: undefined,
      customers: action.customers,
    };
  },
  [SearchCustomersErrorFn.type]: (state: ManualOrderState, action) => ({
    ...state,
    isSearchingCustomers: undefined,
    customers: undefined,
    searchCustomersError: action.error,
  }),
});

export function fetchManualOrderProductsEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchManualOrderProducts.type),
    debounceTime(750),
    switchMap(({queryFilter, addPage}: ManualOrderAction) => {
      const pagination = state$.value.manualOrder.pagination;
      const qf =
        pagination && addPage
          ? {...queryFilter, page: pagination.page + 1}
          : {...queryFilter, page: 0};

      const {querySize, ...searchParams} = qf;

      return getProductsApi({
        ...searchParams,
        product_type: querySize ?? 'FULL',
      }).pipe(
        map(
          ({data, pagination}: {data: Product[], pagination: Pagination}) =>
            new FetchManualOrderProductsSuccess({
              products: data,
              pagination,
              addPage: addPage,
            })
        ),
        buildCatchError(
          FetchManualOrderProductsError,
          'Error al obtener productos'
        )
      );
    })
  );
}

export const fetchShoppingCartPricesEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchShoppingCartPrices.type),
    debounceTime(500),
    switchMap(({shoppingCartPricesRequest}: ManualOrderAction) => {
      if (shoppingCartPricesRequest.items.length)
        return getShoppingCartPricesApi(shoppingCartPricesRequest).pipe(
          map((data: CartSummary) => {
            const {
              items,
              total,
              subTotal,
              promotions,
              globalDiscount: globalDiscountData,
              coupon,
              taxes,
              applicableCoupons,
              couponDiscount,
            } = cartSummaryAdapter(data);

            return new FetchShoppingCartPricesSuccess({
              items,
              coupon,
              promotions,
              total,
              subTotal,
              globalDiscountData,
              taxes,
              applicableCoupons,
              couponDiscount,
            });
          }),
          buildCatchError(FetchShoppingCartPricesErrorFn)
        );
      else
        return of(
          new FetchShoppingCartPricesSuccess({
            items: [],
            coupon: undefined,
            promotions: [],
            total: undefined,
            subTotal: undefined,
            taxes: undefined,
            applicableCoupons: [],
          })
        );
    })
  );

export const fetchProductPriceEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchProductPrice.type),
    debounceTime(500),
    switchMap(({shoppingCartPricesRequest}: ManualOrderAction) => {
      return getShoppingCartPricesApi(shoppingCartPricesRequest).pipe(
        map((data: CartSummary) => {
          const {items} = cartSummaryAdapter(data);
          return new FetchProductPriceSuccess({items});
        }),
        buildCatchError(FetchProductPriceErrorFn)
      );
    })
  );

export const fetchOrderSummaryEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchOrderSummary.type),
    debounceTime(500),
    switchMap(({orderSummaryRequest}: ManualOrderAction) => {
      const {storeId, ...rq} = orderSummaryRequest;
      return getOrderSummaryApi(rq).pipe(
        map((orderSummary: CartSummary) => {
          return new FetchOrderSummarySuccess({orderSummary});
        }),
        buildCatchError(
          FetchOrderSummaryErrorFn,
          'Error al obtener el resumen de la orden'
        )
      );
    })
  );

export const createOrderEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(CreateOrder.type),
    switchMap(({checkoutRequest}: ManualOrderAction) =>
      createOrderApi(checkoutRequest).pipe(
        map((order: Order) => {
          return new CreateOrderSuccess({data: order});
        }),
        buildCatchError(CreateOrderErrorFn)
      )
    )
  );

export const editOrderEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(EditOrder.type),
    switchMap(({checkoutRequest}: ManualOrderAction) =>
      editOrderApi(checkoutRequest.request, {
        route: buildRoute(endpoints.EDIT_ORDER, [checkoutRequest.id]),
      }).pipe(
        map((order: Order) => {
          return new EditOrderSuccess({order, orderId: order.id});
        }),
        buildCatchError(EditOrderErrorFn)
      )
    )
  );

export const createPreOrderEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(CreatePreOrder.type),
    switchMap(({checkoutRequest}: ManualOrderAction) =>
      createPreOrderApi(checkoutRequest).pipe(
        map((order: Order) => {
          return new CreatePreOrderSuccess({data: order});
        }),
        buildCatchError(CreatePreOrderErrorFn)
      )
    )
  );

export const editPreOrderEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(EditPreOrder.type),
    switchMap(({checkoutRequest}: ManualOrderAction) =>
      editPreOrderApi(checkoutRequest.request, {
        route: buildRoute(endpoints.EDIT_PRE_ORDER, [checkoutRequest.id]),
      }).pipe(
        map((order: Order) => {
          return new EditPreOrderSuccess({order, orderId: order.id});
        }),
        buildCatchError(EditPreOrderErrorFn)
      )
    )
  );

export function searchCustomersEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(SearchCustomers.type),
    debounceTime(250),
    switchMap(({searchFilter}: ManualOrderAction) => {
      let query = undefined;
      if (searchFilter) query = searchFilter;
      return getCustomersApi(query).pipe(
        map(
          ({data, pagination}: {data: CrmCustomer[], pagination: Pagination}) =>
            new SearchCustomersSuccess({customers: data, pagination})
        ),
        buildCatchError(SearchCustomersErrorFn, 'Error al obtener los clientes')
      );
    })
  );
}
